import { Anchor, Badge, Button, Card, Flex, Grid, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAward,
  IconBell,
  IconChartLine,
  IconCloudDownload,
  IconFile,
  IconFileText,
  IconHistory,
  IconMail,
  IconPhone,
  IconRobot,
  IconSettings,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import algorithms from '../../constants/Algorithms';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import ProductFeedExportModal from '../Modals/ProductFeedExportModal';
import ProductFeedStats from './ProductFeedStats';

const ProductFeedCard = ({ productFeed, stats, period }) => {
  const [open, setOpen] = useState(true);
  const { accountId, domainId } = useParams();
  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;

  const location = useLocation();

  const fetch = useFetch();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [segments, setSegments] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments?scope=internal`)
      .then(({ segments }) => {
        setSegments(segments);
      })
      .catch(setError);
  }, [fetch, accountId, domainId]);

  let badges = [];

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (productFeed.scope === 'web' && productFeed.recommenders) {
    const pagePerRecommenders = productFeed.recommenders.reduce((accu, recommender) => {
      if (accu[recommender.pageId] === undefined) {
        accu[recommender.pageId] = { page: recommender.page, recommenders: [] };
      }
      accu[recommender.pageId].recommenders.push(recommender);
      return accu;
    }, {});

    badges = Object.values(pagePerRecommenders).flatMap(({ page, recommenders }, index) => {
      const pageBadges = [
        <Badge key={`page-${index}`} variant="default">
          <IconFile size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          <Anchor
            href={`#/accounts/${accountId}/domains/${domainId}/pages?page=${page.id}&type=recommenders`}
            size="xs"
          >
            {page.name}
          </Anchor>
        </Badge>,
      ];
      recommenders.forEach((recommender, rIndex) => {
        pageBadges.push(
          <Badge key={`rec-${index}-${rIndex}`} variant="default">
            <IconAward size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            <Anchor
              href={`#/accounts/${accountId}/domains/${domainId}/pages/${recommender.pageId}/recommenders/${recommender.id}/details`}
              size="xs"
            >
              {recommender.name}
            </Anchor>
          </Badge>
        );
      });
      return pageBadges;
    });
  }

  return (
    <>
      {exportModalOpen && (
        <ProductFeedExportModal
          isOpen={exportModalOpen}
          close={() => setExportModalOpen(false)}
          scheduled={() => {
            setExportModalOpen(false);
            notifications.show({
              title: 'Export Scheduled',
              message:
                'Your export has been scheduled for processing. You will receive an email with the export once it has been processed.',
              color: 'success',
            });
          }}
          productFeed={productFeed}
          segments={segments}
        />
      )}
      <Card key={productFeed.id} mb="md" onClick={() => setOpen(!open)} withBorder>
        <Card.Section inheritPadding py="sm">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <Anchor
                href={`#${location.pathname}/${productFeed.id}/details`}
                fw={600}
                mb="xs"
                style={{ display: 'block' }}
              >
                {productFeed.name}
              </Anchor>
              <Flex gap="xs" wrap="wrap">
                <Badge variant="default">
                  {productFeed.scope === 'web' ? (
                    <IconFileText size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  ) : (
                    <IconSettings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  )}
                  {prettifiedLabel(productFeed.scope)}
                </Badge>
                {algorithms[productFeed.algorithm] ? (
                  <Badge variant="default">
                    <IconRobot size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {algorithms[productFeed.algorithm].name}
                  </Badge>
                ) : null}
                {badges}
                {productFeed.emails &&
                  productFeed.emails.length > 0 &&
                  productFeed.emails.map((email, index) => (
                    <Badge key={`email-${index}`} variant="default">
                      <IconMail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      <Anchor href={`#/accounts/${accountId}/domains/${domainId}/emails/${email.id}/details`} size="xs">
                        {email.name}
                      </Anchor>
                    </Badge>
                  ))}
                {productFeed.viberMessages &&
                  productFeed.viberMessages.length > 0 &&
                  productFeed.viberMessages.map((message, index) => (
                    <Badge key={`viber-${index}`} variant="default">
                      <IconPhone size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      <Anchor
                        href={`#/accounts/${accountId}/domains/${domainId}/viber/${message.id}/details`}
                        size="xs"
                      >
                        {message.name}
                      </Anchor>
                    </Badge>
                  ))}
                {productFeed.pushNotifications &&
                  productFeed.pushNotifications.length > 0 &&
                  productFeed.pushNotifications.map((message, index) => (
                    <Badge key={`push-${index}`} variant="default">
                      <IconBell size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      <Anchor href={`#/accounts/${accountId}/domains/${domainId}/push/${message.id}/details`} size="xs">
                        {message.name}
                      </Anchor>
                    </Badge>
                  ))}
                <Tooltip
                  label={
                    <>
                      <div>Created: {new Date(productFeed.createdAt).toLocaleString()}</div>
                      <div>Updated: {new Date(productFeed.updatedAt).toLocaleString()}</div>
                    </>
                  }
                  position="top"
                  withArrow
                >
                  <Badge variant="default" style={{ cursor: 'pointer' }}>
                    <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                  </Badge>
                </Tooltip>
              </Flex>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Flex justify="flex-end" gap="xs">
                <Button component="a" href={`#${location.pathname}/${productFeed.id}/analytics`} size="xs">
                  Statistics <IconChartLine size={14} style={{ marginLeft: 4 }} />
                </Button>
                <Button size="xs" onClick={() => setExportModalOpen(true)}>
                  Export <IconCloudDownload size={14} style={{ marginLeft: 4 }} />
                </Button>
              </Flex>
            </Grid.Col>
          </Grid>
        </Card.Section>
        <Card.Section inheritPadding py="sm">
          <Grid>
            <ProductFeedStats productFeed={productFeed} stats={stats} period={period} currency={domainCurrency} />
          </Grid>
        </Card.Section>
      </Card>
    </>
  );
};

export default ProductFeedCard;
